import { useLayoutEffect, useMemo, useRef, useState } from "react";
import ModernTemplate from "@/components/templates/ModernTemplate";
import ClassicTemplate from "@/components/templates/ClassicTemplate";
import MinimalTemplate from "@/components/templates/MinimalTemplate";
import CreativeTemplate from "@/components/templates/CreativeTemplate";
import ExecutiveTemplate from "@/components/templates/ExecutiveTemplate";
import ATSPlainTemplate from "@/components/templates/ATSPlainTemplate";
import ATSCompactTemplate from "@/components/templates/ATSCompactTemplate";
import TwoColumnTemplate from "@/components/templates/TwoColumnTemplate";
import PhotoHeaderTemplate from "@/components/templates/PhotoHeaderTemplate";
import { RESUME_SAMPLES } from "@/lib/initialData";

const TEMPLATES = [
  { id: "modern",       name: "Modern",       Component: ModernTemplate,     photo: false, sample: "software-engineer", isFree: true,  tag: "Grotesk · Sharp" },
  { id: "classic",      name: "Classic",      Component: ClassicTemplate,    photo: false, sample: "accountant",                        tag: "Serif · Balanced" },
  { id: "minimal",      name: "Minimal",      Component: MinimalTemplate,    photo: false, sample: "sales-manager",                     tag: "Mono · Restrained" },
  { id: "creative",     name: "Creative",     Component: CreativeTemplate,   photo: false, sample: "software-engineer",                 tag: "Editorial · Bold" },
  { id: "executive",    name: "Executive",    Component: ExecutiveTemplate,  photo: false, sample: "sales-manager",                     tag: "Two-column · Dense" },
  { id: "two-column",   name: "Two-column",   Component: TwoColumnTemplate,  photo: true,  sample: "doctor",                            tag: "Sidebar · Dark" },
  { id: "photo-header", name: "Photo Header", Component: PhotoHeaderTemplate,photo: true,  sample: "student",                           tag: "Banner · Friendly" },
  { id: "ats-plain",    name: "ATS Plain",    Component: ATSPlainTemplate,   ats: true,    sample: "software-engineer", isFree: true,  tag: "Arial · Machine-safe" },
  { id: "ats-compact",  name: "ATS Compact",  Component: ATSCompactTemplate, ats: true,    sample: "accountant",                        tag: "Calibri · Tight" },
];

// Exported so other surfaces (e.g. the `/get-started` onboarding gallery)
// can render the same 9-template roster without duplicating this array.
// Kept as a stable read-only export — mutating it will break both the
// in-builder picker and the onboarding gallery.
export const TEMPLATE_LIST = TEMPLATES;
export const TEMPLATE_IDS = new Set(TEMPLATES.map((t) => t.id));
export const isValidTemplateId = (id) => TEMPLATE_IDS.has(id);

// Single source of truth for "which templates are free to download".
// Consumed by Builder.js (payment gating, watermark logic, CTA copy) and
// by the template gallery for the FREE badge.
export const FREE_TEMPLATE_IDS = new Set(
  TEMPLATES.filter((t) => t.isFree).map((t) => t.id)
);
export const isFreeTemplate = (templateId) => FREE_TEMPLATE_IDS.has(templateId);

// Templates whose visual identity depends on design elements bleeding to
// the physical page edge (dark sidebar, coloured header banner, red
// accent strip, etc.). For these, the standard 2 cm `@page` margin
// would inset the decorative element visibly, so we drop the outer
// paper padding + browser page margin and rely on each template's inner
// text containers to provide the 2 cm text safe area instead. Consumers:
//   • ResumePreview.js — sets data-template on `.paper`, emits per-template @page
//   • pdf.js           — reads the flag before injecting the print @page rule
//   • index.css        — .paper[data-template=<id>] { padding: 0 } preview override
export const FULL_BLEED_TEMPLATE_IDS = new Set(["two-column", "photo-header", "creative"]);
export const isFullBleedTemplate = (templateId) => FULL_BLEED_TEMPLATE_IDS.has(templateId);

// A4 native dimensions used when rendering the shrunk template preview.
// The wrapper below is styled with an aspect-ratio matching this, and the
// inner scaled node fills 100% of the wrapper via CSS transform.
const PAPER_W = 794;
const PAPER_H = 1123;

/**
 * Renders a single template thumbnail: an actual scaled-down render of the
 * template populated with a role-based sample résumé so users can visualise
 * the layout instead of squinting at generic icon lines.
 *
 * The inner render is a fixed 794×1123 px A4 page — we scale it via
 * `transform: scale(...)` using a ResizeObserver so it always fills the
 * wrapper edge-to-edge no matter what column width the grid gives us.
 * This gives compact in-Builder pickers small crisp previews AND the
 * larger `/get-started` gallery cards genuinely readable previews from
 * the same component. Falls back to a sensible default until the
 * observer fires on mount.
 *
 * Exported so both the compact in-builder picker and the full-page
 * `/get-started` onboarding gallery share the same rendering pipeline
 * (single source of truth — no drift between the two surfaces).
 */
export function TemplateThumbnail({ Component, sampleId }) {
  const sampleResume = useMemo(
    () => RESUME_SAMPLES[sampleId] || Object.values(RESUME_SAMPLES)[0],
    [sampleId]
  );
  const outerRef = useRef(null);
  const [scale, setScale] = useState(0.32);

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setScale(w / PAPER_W);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={outerRef}
      className="w-full aspect-[794/1123] relative overflow-hidden bg-white"
      style={{ isolation: "isolate" }}
    >
      <div
        className="absolute top-0 left-0 origin-top-left pointer-events-none select-none"
        style={{
          width: `${PAPER_W}px`,
          height: `${PAPER_H}px`,
          transform: `scale(${scale})`,
        }}
      >
        <Component resume={sampleResume} />
      </div>
    </div>
  );
}

export default function TemplatePicker({ value, onChange }) {
  return (
    <div data-testid="template-picker">
      <div className="label-mono mb-3">Choose a template</div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Each preview uses a role-based sample so you can gauge how the layout
        handles real content. Your own résumé data appears in the live preview
        pane once you pick one.
      </div>
      <div
        className="grid grid-cols-2 md:grid-cols-3 gap-3"
      >
        {TEMPLATES.map((t) => {
          const active = t.id === value;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              className={`group text-left p-2 border-2 transition-all rounded-sm ${
                active
                  ? "border-[#2400FF] bg-[#2400FF]/[0.04]"
                  : "border-black/10 hover:border-black/40"
              }`}
              data-testid={`template-${t.id}`}
              aria-pressed={active}
            >
              <div className="relative border border-black/10 shadow-sm overflow-hidden">
                <TemplateThumbnail Component={t.Component} sampleId={t.sample} />
                {t.photo && (
                  <span className="absolute top-1 right-1 text-[8px] font-mono uppercase tracking-widest bg-[#2400FF] text-white px-1 py-0.5 z-10">
                    Photo
                  </span>
                )}
                {t.ats && (
                  <span className="absolute top-1 right-1 text-[8px] font-mono uppercase tracking-widest bg-black text-white px-1 py-0.5 z-10">
                    ATS
                  </span>
                )}
                {t.isFree && (
                  <span
                    className="absolute top-1 left-1 text-[8px] font-mono font-bold uppercase tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 z-10 rounded-sm shadow-sm"
                    data-testid={`template-${t.id}-free-badge`}
                    aria-label="Free download"
                  >
                    Free
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className={`font-heading font-semibold text-xs tracking-tight ${active ? "text-[#2400FF]" : ""}`}>
                  {t.name}
                </div>
                {active && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-[#2400FF]">
                    ✓ Active
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
